import { borderRadius, colors } from '@/theme'
import type { TimelineEventCategory, TimelineEventSource, TimelineFilterState } from '@/types/timeline'
import CancelOutlined from '@mui/icons-material/CancelOutlined'
import FilterList from '@mui/icons-material/FilterList'
import Search from '@mui/icons-material/Search'
import { Box, Chip, InputAdornment, Paper, TextField, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { enGB } from 'date-fns/locale/en-GB'
import { ALL_CATEGORIES, ALL_SOURCES, type TimelineCategory } from './config'

interface TimelineFiltersProps {
  filters: TimelineFilterState
  onFiltersChange: (filters: TimelineFilterState) => void
  excludedFilters?: TimelineCategory[]
}

const chipSx = (selected: boolean) => ({
  fontWeight: 500,
  fontSize: '12px',
  cursor: 'pointer',
  transition: 'all 0.15s',
  ...(selected
    ? {
        bgcolor: colors.brand.primary,
        color: '#FFFFFF',
        border: `1px solid ${colors.brand.primary}`,
        '&:hover': { bgcolor: colors.brand.primaryHover }
      }
    : {
        bgcolor: colors.background.paper,
        color: colors.text.primary,
        border: `1px solid ${colors.border.default}`,
        '&:hover': { borderColor: colors.border.hover, bgcolor: colors.background.surface }
      })
})

export default function TimelineFilters({ filters, onFiltersChange, excludedFilters = [] }: TimelineFiltersProps) {
  const toggleCategory = (category: TimelineEventCategory) => {
    const updated = filters.categories.includes(category)
      ? filters.categories.filter(c => c !== category)
      : [...filters.categories, category]
    onFiltersChange({ ...filters, categories: updated })
  }

  const toggleSource = (source: TimelineEventSource) => {
    const updated = filters.sources.includes(source)
      ? filters.sources.filter(s => s !== source)
      : [...filters.sources, source]
    onFiltersChange({ ...filters, sources: updated })
  }

  const filteredCategories = ALL_CATEGORIES.filter(category => !excludedFilters.includes(category))

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Paper elevation={0} sx={{ border: `1px solid ${colors.border.default}`, borderRadius: borderRadius.lg, p: 3 }}>
        <TextField
          value={filters.search}
          onChange={e => onFiltersChange({ ...filters, search: e.target.value })}
          placeholder='Search...'
          variant='outlined'
          size='small'
          fullWidth
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': { borderRadius: borderRadius.sm, bgcolor: colors.background.paper }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search sx={{ color: colors.text.muted, fontSize: 20 }} />
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterList sx={{ color: colors.brand.primary, fontSize: 22 }} />
          <Typography sx={{ fontSize: '18px', fontWeight: 600, color: colors.brand.primary }}>Filters</Typography>
        </Box>

        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: colors.text.secondary, mb: 1.5 }}>
          Filter by:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {filteredCategories.map(category => {
            const selected = filters.categories.includes(category)
            return (
              <Chip
                key={category}
                label={category}
                size='medium'
                onClick={() => toggleCategory(category)}
                onDelete={selected ? () => toggleCategory(category) : undefined}
                deleteIcon={
                  selected ? (
                    <CancelOutlined sx={{ fontSize: '16px !important', color: '#fff !important' }} />
                  ) : undefined
                }
                sx={chipSx(selected)}
              />
            )
          })}
        </Box>

        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: colors.text.secondary, mb: 1.5 }}>
          Source:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {ALL_SOURCES.map(source => {
            const selected = filters.sources.includes(source)
            return (
              <Chip
                key={source}
                label={source}
                size='medium'
                onClick={() => toggleSource(source)}
                onDelete={selected ? () => toggleSource(source) : undefined}
                deleteIcon={
                  selected ? (
                    <CancelOutlined sx={{ fontSize: '16px !important', color: '#fff !important' }} />
                  ) : undefined
                }
                sx={chipSx(selected)}
              />
            )
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: colors.text.secondary }}>Date Range:</Typography>
          {(filters.dateFrom || filters.dateTo) && (
            <Typography
              onClick={() => onFiltersChange({ ...filters, dateFrom: null, dateTo: null })}
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: colors.brand.primary,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Clear
            </Typography>
          )}
        </Box>

        <DateRow
          label='From:'
          value={filters.dateFrom}
          onChange={date => onFiltersChange({ ...filters, dateFrom: date })}
          sx={{ mb: 2 }}
        />
        <DateRow label='To:' value={filters.dateTo} onChange={date => onFiltersChange({ ...filters, dateTo: date })} />
      </Paper>
    </LocalizationProvider>
  )
}

function DateRow({
  label,
  value,
  onChange,
  sx
}: {
  label: string
  value: Date | null
  onChange: (date: Date | null) => void
  sx?: object
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ...sx }}>
      <Typography sx={{ fontSize: '13px', color: colors.text.secondary, minWidth: '36px' }}>{label}</Typography>
      <DatePicker
        value={value}
        onChange={onChange}
        format='dd/MM/yyyy'
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            placeholder: 'DD/MM/YYYY',
            sx: { '& .MuiOutlinedInput-root': { borderRadius: borderRadius.sm, fontSize: '13px' } }
          }
        }}
      />
    </Box>
  )
}
